import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import StatusCard from 'components/Estimate/StatusCard/StatusCard';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { saleSelectors } from 'store/sale/selectors';
import { CompanyInfo } from 'types/CompanyInfo';
import { Sale } from 'types/Sale';
import { PageVariant, SALE_MAP, StatusCardType } from 'types/constants';

interface StatusCardRendererProps {
  insightData: {
    pageVariant: PageVariant;
  };
  shouldShowChangeOrderFeatures: () => boolean;
  getDisplayAmount: () => number;
  getStatusCardTitle: () => React.ReactNode;
  sale: Sale | {};
  companyInfo: CompanyInfo | {};
  pdfStatus: {
    downloading: boolean;
    error: boolean;
  };
  config: {
    endpoints?: {
      cdn?: string;
    };
  };
  handleDownloadPDF: () => void;
  estimateStatus?: string | null;
}

export const renderStatusCard = ({
  insightData,
  shouldShowChangeOrderFeatures,
  getDisplayAmount,
  getStatusCardTitle,
  sale,
  companyInfo,
  pdfStatus,
  handleDownloadPDF,
  estimateStatus,
}: StatusCardRendererProps) => {
  const isChangeOrder = shouldShowChangeOrderFeatures();
  const displayAmount = getDisplayAmount();
  const title = getStatusCardTitle();

  // Get data using selectors
  const companyName = companyInfoSelectors.nameSelector(companyInfo as CompanyInfo) || '';
  const companyLocale = companyInfoSelectors.localeSelector(companyInfo as CompanyInfo) || '';
  const amount = saleSelectors.amountSelector(sale as Sale) || 0;
  const currency = saleSelectors.currencySelector(sale as Sale) || 'USD';

  // Determine if this is a declined/expired variant or if status is REJECTED
  const isDeclinedVariant =
    insightData.pageVariant === PageVariant.ESTIMATE_DECLINED ||
    insightData.pageVariant === PageVariant.DEPOSIT_DECLINED ||
    insightData.pageVariant === PageVariant.ESTIMATE_EXPIRED ||
    estimateStatus === SALE_MAP.STATUS.REJECTED;

  // Get the appropriate date based on variant
  const actionDate =
    insightData.pageVariant === PageVariant.ESTIMATE_EXPIRED
      ? (sale as Sale).expirationDate || ''
      : saleSelectors.estimateActionDate(sale as Sale) || '';

  // Determine the type and message
  const type = isDeclinedVariant ? StatusCardType.DECLINED : StatusCardType.ACCEPTED;
  const total = isDeclinedVariant ? amount : (displayAmount as number);

  const message =
    insightData.pageVariant === PageVariant.ESTIMATE_EXPIRED ? (
      <FormattedMessage
        id="ESTIMATE_EXPIRED_MESSAGE"
        defaultMessage="Please reach out {company} for more details"
        values={{ company: companyName }}
      />
    ) : null;

  return (
    <StatusCard
      type={type}
      title={title}
      companyName={companyName}
      acceptedDate={actionDate}
      total={total}
      currency={currency}
      companyLocale={companyLocale}
      isLoadingEstimate={pdfStatus.downloading}
      isPDFError={pdfStatus.error}
      viewEstimateHandler={handleDownloadPDF}
      isChangeOrder={isChangeOrder}
      message={message}
    />
  );
};

export const useStatusCard = (props: StatusCardRendererProps) => {
  return useCallback(
    () => renderStatusCard(props),
    [
      props.insightData,
      props.shouldShowChangeOrderFeatures,
      props.getDisplayAmount,
      props.getStatusCardTitle,
      props.sale,
      props.companyInfo,
      props.pdfStatus,
      props.config,
      props.handleDownloadPDF,
      props.estimateStatus,
    ]
  );
};
